<div class="modal-header">
  <h4 class="modal-title header-5">
    {{ currentStep === 1 ? 'Generate Test Data' : 'Select test cases' }}
  </h4>
  <button type="button" class="accello-icon-btn" (click)="cancel()" aria-label="Close">
    <img src="assets/images/virtusa-icons/close.svg" alt="Close" />
  </button>
</div>

<div class="modal-body selection-panel" [class.gtd-step2-body]="currentStep === 2">
  @if (currentStep === 1) {
    <div class="info-box">
      <div class="d-flex gap-2 align-items-center mb-1">
        <div class="info-box-icon-wrap">
          <img
            class="info-box-icon"
            src="assets/images/virtusa-icons/bulb.svg"
            alt=""
            width="16"
            height="16"
          />
        </div>
        <span class="info-box-title">Recommended Actions</span>
      </div>
      <p class="info-box-body mb-0">
        Data sources are managed in
        <strong>Project Settings → Connections</strong>.
        Upload your source files there to make them available for generation.
      </p>
    </div>

    <span class="section-label">Select Data Schema From <span class="gtd-required">*</span></span>

    <div class="selection-box-wrapper d-flex">
      <div
        class="selection-box"
        role="button"
        tabindex="0"
        (click)="selectSource('manualFile')"
        (keydown.enter)="selectSource('manualFile')"
        [class.active-source]="selectedSource === 'manualFile'"
        [attr.aria-pressed]="selectedSource === 'manualFile'"
      >
        <span class="sb-title">From schema file</span>
        <span class="sb-description">SQL, CSV, or Excel</span>
      </div>

      <div
        class="selection-box"
        role="button"
        tabindex="0"
        (click)="selectSource('liveDb')"
        (keydown.enter)="selectSource('liveDb')"
        [class.active-source]="selectedSource === 'liveDb'"
        [class.disabled-source]="!liveDbEnabled"
        [attr.aria-pressed]="selectedSource === 'liveDb'"
        [attr.aria-disabled]="!liveDbEnabled"
        [title]="liveDbEnabled ? 'From live DB' : 'Live DB not available'"
      >
        <span class="sb-title">From live DB</span>
        <span class="sb-description">SQL DB</span>
      </div>
    </div>

    @if (loadError) {
      <div class="gtd-error">Failed to load schema files.</div>
    }

    @if (selectedSource === 'manualFile') {
      @if (!showAddSchemaForm) {
        <div class="d-flex align-content-start flex-row gap-3">
          <div class="inp-group-box">
            <label class="inp-label mt-3 d-flex align-items-center gap-2">
              Data Schema File Type <span class="gtd-required">*</span>
              @if (isLoading) {
                <span class="spinner-border spinner-border-sm gtd-field-spinner" role="status" aria-label="Loading schema files"></span>
              }
            </label>
            <select
              class="mt-2 form-select"
              [(ngModel)]="selectedFileType"
              (ngModelChange)="onFileTypeChange()"
              [disabled]="isLoading"
            >
              <option *ngFor="let ft of fileTypes" [value]="ft">{{ ft }}</option>
            </select>
          </div>

          <div class="inp-group-box">
            <label class="inp-label mt-3">File Name <span class="gtd-required">*</span></label>
            <select class="mt-2 form-select" [(ngModel)]="selectedFile" [disabled]="isLoading">
              <option [ngValue]="null">Select file</option>
              <option *ngFor="let f of filteredSchemas" [ngValue]="f">
                {{ f?.meta?.fileName || f?.connectionName }}
              </option>
            </select>
          </div>
        </div>

        <button type="button" class="gtd-add-link" (click)="openAddSchemaForm()" [disabled]="isLoading">
          + Add a new schema file
        </button>
      } @else {
        <div class="gtd-inline-form">
          <div class="gtd-inline-form-header">
            <span class="inp-label">Data Schema File Type <span class="gtd-required">*</span></span>
            <div class="gtd-inline-form-actions">
              <button
                type="button"
                class="gtd-inline-save"
                (click)="saveNewSchema()"
                [disabled]="!canSaveNewSchema"
              >
                @if (isUploading) {
                  <span class="spinner-border spinner-border-sm" aria-hidden="true"></span>
                } @else {
                  <mat-icon>check</mat-icon>
                }
              </button>
              <button type="button" class="gtd-inline-cancel" (click)="cancelAddSchemaForm()" [disabled]="isUploading">
                <mat-icon>close</mat-icon>
              </button>
            </div>
          </div>

          <select class="form-select mb-3" [(ngModel)]="newSchemaType">
            <option *ngFor="let ft of fileTypes" [value]="ft">{{ ft }}</option>
          </select>

          <label class="inp-label mb-1">Schema File <span class="gtd-required">*</span></label>
          <label class="gtd-file-drop">
            <mat-icon class="gtd-file-drop-icon">add</mat-icon>
            <span class="gtd-file-drop-text">
              {{ newSchemaFile ? newSchemaFile.name : 'Add your schema file here' }}
            </span>
            <input
              type="file"
              class="gtd-file-input"
              [accept]="newSchemaAccept"
              (change)="onNewSchemaFileSelected($event)"
            />
          </label>

          @if (uploadError) {
            <p class="gtd-error mt-2 mb-0">{{ uploadError }}</p>
          }
        </div>
      }
    }

    @if (selectedSource === 'liveDb') {
      @if (!showAddDbForm) {
        <div class="d-flex align-content-start flex-row gap-3">
          <div class="inp-group-box gtd-db-conn-box">
            <label class="inp-label mt-3 d-flex align-items-center gap-2">
              Database Connection <span class="gtd-required">*</span>
              @if (isLoadingDbConnections) {
                <span
                  class="spinner-border spinner-border-sm gtd-field-spinner"
                  role="status"
                  aria-label="Loading database connections"
                ></span>
              }
            </label>
            <select
              class="mt-2 form-select"
              [(ngModel)]="selectedDbConnection"
              [disabled]="isLoadingDbConnections"
            >
              <option [ngValue]="null">Select connection</option>
              <option *ngFor="let conn of dbConnections" [ngValue]="conn">
                {{ conn?.connectionName }}
              </option>
            </select>
          </div>
        </div>

        @if (dbConnectionsError) {
          <div class="gtd-error mt-2">Failed to load database connections.</div>
        }

        <button
          type="button"
          class="gtd-add-link"
          (click)="openAddDbForm()"
          [disabled]="isLoadingDbConnections"
        >
          + Add a new connection
        </button>
      } @else {
        <div class="gtd-inline-form gtd-db-form">
          <div class="gtd-inline-form-header">
            <span class="inp-label">Add DB Connection</span>
            <div class="gtd-inline-form-actions">
              <button
                type="button"
                class="gtd-inline-save"
                (click)="saveNewDbConnection()"
                [disabled]="!canSaveNewDbConnection"
              >
                @if (isSavingDbConnection) {
                  <span class="spinner-border spinner-border-sm" aria-hidden="true"></span>
                } @else {
                  <mat-icon>check</mat-icon>
                }
              </button>
              <button
                type="button"
                class="gtd-inline-cancel"
                (click)="cancelAddDbForm()"
                [disabled]="isSavingDbConnection"
              >
                <mat-icon>close</mat-icon>
              </button>
            </div>
          </div>

          <app-connection-modal
            [configs]="dbConnectionConfigs"
            [embedded]="true"
            [hideFooter]="true"
            [saving]="isSavingDbConnection"
            (saved)="onDbConnectionSaved($event)"
          ></app-connection-modal>
        </div>
      }
    }
  } @else {
    @if (isLoadingSuites) {
      <div class="gtd-loading-wrap">
        <app-loading [text]="'Loading test suites...'"></app-loading>
      </div>
    } @else if (suitesError) {
      <div class="gtd-error">Failed to load test suites.</div>
    } @else if (!accordionSections.length) {
      <div class="gtd-empty">No test suites available.</div>
    } @else {
      <app-accordion-table-view
        [sections]="accordionSections"
        typeId="tdg_generate_test_cases"
        [itemLabel]="'Test Cases'"
        [showHeader]="false"
        [showPagination]="false"
        [singleSectionSelection]="true"
        (sectionExpand)="onSectionExpand($event)"
        (tableRowSelect)="onTableRowSelect($event)"
      ></app-accordion-table-view>
    }
  }
</div>

<div class="modal-footer gtd-footer">
  <span class="gtd-step-label">{{ stepLabel }}</span>
  <div class="gtd-footer-actions">
    @if (currentStep === 2) {
      <button type="button" class="btn-accello-secondary" (click)="goBack()">Back</button>
    } @else {
      <button type="button" class="btn-accello-secondary" (click)="cancel()">Cancel</button>
    }
    <button type="button" class="btn-accello-primary-light" [disabled]="!canContinue" (click)="continue()">
      {{ continueLabel }}
    </button>
  </div>
</div>
